import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Sparkles, Gift, Tag, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import AutumnLeaves, { CornerLeaves } from "./AutumnLeaves";
import { useSaleProductNames } from "./SalePrice";
import SaleCountdown from "./SaleCountdown";
import GhostMark from "./GhostMark";

// Rotating promo banner on /products. Slides come from the admin-editable
// `promo_slides` collection (enabled + in-date-range, sorted by `order`).
// All slides are stacked in one grid cell and cross-faded, so the banner
// height never jumps between slides. Auto-rotation pauses on hover/focus.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEGACY_DISMISS_KEY = "luviarx_promo_rotator_dismissed_v1";
const AUTO_ROTATE_MS = 7000;
const ICONS = { Sparkles, Gift, Tag };

const C = {
  frame: "#F5EAE6",
  card: "#FDFAF9",
  accent: "#8B2252",
  ink: "#1C1618",
  muted: "#9a7a80",
};

export const PromoBannerRotator = () => {
  const [dbSlides, setSlides] = useState([]);
  const { activeSeasonal } = useCart();
  const saleNames = useSaleProductNames();
  // The live sale (admin "Sales" tab) always leads the rotation with its own slide.
  const slides = useMemo(() => {
    if (!activeSeasonal) return dbSlides;
    const sale = {
      id: `sale-${activeSeasonal.id}`,
      type: "sale",
      eyebrow_label: activeSeasonal.name,
      headline_accent: `${activeSeasonal.percent}% off`,
      headline_accent_color: activeSeasonal.accent_color || C.accent,
      headline: saleNames,
      ghost: activeSeasonal.icon === "ghost",
      icon: "Tag",
      end_date: activeSeasonal.end_date,
      messages: [
        activeSeasonal.description || "Every size, applied automatically at checkout.",
        activeSeasonal.allow_first10
          ? "New customers can stack FIRST10 for an extra 10% off their first order."
          : "Cannot be combined with any other discount.",
      ],
      cta_label: "Applied automatically",
    };
    return [sale, ...dbSlides.filter((s) => s.eyebrow_label !== activeSeasonal.name)];
  }, [activeSeasonal, saleNames, dbSlides]);
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);

  useEffect(() => {
    try { window.localStorage.removeItem(LEGACY_DISMISS_KEY); } catch { /* ignore */ }
    axios.get(`${API}/promo-slides`)
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setSlides(list.filter((s) => s?.type !== "email_capture"));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const t = setTimeout(() => setSlide((s) => (s + 1) % slides.length), AUTO_ROTATE_MS);
    return () => clearTimeout(t);
  }, [paused, slides.length, slide]);

  const goTo = useCallback((i) => {
    if (slides.length === 0) return;
    setSlide(((i % slides.length) + slides.length) % slides.length);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const hasMultiple = slides.length > 1;
  const current = Math.min(slide, slides.length - 1);

  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) goTo(current + (dx < 0 ? 1 : -1));
    touchX.current = null;
  };

  return (
    <section
      data-testid="promo-banner-rotator"
      className="container-x pt-6 md:pt-10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
      aria-label="Current promotions"
    >
      <div className="rounded-[26px] p-1.5 sm:p-2" style={{ background: C.frame }}>
        <div
          className="relative overflow-hidden rounded-[20px] border"
          style={{ background: C.card, borderColor: "rgba(139,34,82,0.08)" }}
        >
          <span
            aria-hidden
            className={`pointer-events-none absolute -right-24 -top-24 w-72 h-72 rounded-full hidden ${activeSeasonal ? "" : "sm:block"}`}
            style={{ background: C.frame, opacity: 0.45 }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute left-0 top-8 bottom-8 w-[3px] rounded-full"
            style={{ background: C.accent, opacity: 0.85 }}
          />
          {activeSeasonal && <><CornerLeaves bottom={false} /><AutumnLeaves count={5} className="hidden sm:block opacity-50" /></>}

          <div className="relative grid">
            {slides.map((s, i) => (
              <div
                key={s.id || i}
                data-testid={i === current ? "promo-rotator-slot" : `promo-rotator-slide-${i}`}
                aria-hidden={i !== current}
                className={`promo-rot-slide [grid-area:1/1] px-6 sm:px-10 md:px-12 pt-7 md:pt-9 ${
                  hasMultiple ? "pb-4" : "pb-7 md:pb-9"
                } ${i === current ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1.5 pointer-events-none"}`}
              >
                <PromoCodeSlide slide={s} active={i === current} onClaimed={() => hasMultiple && goTo(current + 1)} />
              </div>
            ))}
          </div>

          {hasMultiple && (
            <RotatorControls
              count={slides.length}
              current={current}
              paused={paused}
              onGo={goTo}
            />
          )}
        </div>
      </div>

      <style>{`
        .promo-rot-slide { transition: opacity 700ms ease, transform 700ms cubic-bezier(.2,.7,.2,1); }
        @keyframes promoDotFill { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes promoMsgIn { from { opacity: 0; transform: translateY(3px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) {
          .promo-rot-slide { transition: none; }
          .promo-dot-fill, .promo-msg { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

const RotatorControls = ({ count, current, paused, onGo }) => (
  <div className="relative flex items-center justify-between px-6 sm:px-10 md:px-12 pb-5 md:pb-6">
    <div data-testid="promo-rotator-dots" className="flex items-center gap-2">
      {Array.from({ length: count }).map((_, i) => {
        const active = i === current;
        return (
          <button
            key={i}
            type="button"
            onClick={() => onGo(i)}
            data-testid={`promo-rotator-dot-${i}`}
            aria-label={`Go to promotion ${i + 1}`}
            aria-current={active}
            className="group py-2.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8B2252]/50 rounded-full"
          >
            <span
              className={`relative block h-1.5 rounded-full overflow-hidden transition-[width,background-color] duration-500 ${
                active ? "w-8" : "w-1.5 group-hover:bg-[#9a7a80]/70"
              }`}
              style={{ background: active ? "rgba(139,34,82,0.18)" : "rgba(154,122,128,0.35)" }}
            >
              {active && (
                <span
                  key={current}
                  className="promo-dot-fill absolute inset-0 origin-left rounded-full"
                  style={{
                    background: C.accent,
                    animation: `promoDotFill ${AUTO_ROTATE_MS}ms linear forwards`,
                    animationPlayState: paused ? "paused" : "running",
                  }}
                />
              )}
            </span>
          </button>
        );
      })}
    </div>
    <div className="flex items-center gap-3">
      <span
        data-testid="promo-rotator-counter"
        className="hidden sm:inline text-[11px] tabular-nums tracking-[0.18em]"
        style={{ color: C.muted }}
      >
        {String(current + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
      </span>
      <ArrowBtn dir="prev" onClick={() => onGo(current - 1)} />
      <ArrowBtn dir="next" onClick={() => onGo(current + 1)} />
    </div>
  </div>
);

const ArrowBtn = ({ dir, onClick }) => {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`promo-rotator-${dir}`}
      aria-label={dir === "prev" ? "Previous promotion" : "Next promotion"}
      className="w-8 h-8 rounded-full border flex items-center justify-center transition-colors duration-300 hover:bg-[#8B2252] hover:text-white hover:border-[#8B2252] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B2252]/50"
      style={{ borderColor: "rgba(139,34,82,0.18)", color: C.accent }}
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
    </button>
  );
};

// ------------------------- Promo slide -------------------------- //
const PromoCodeSlide = ({ slide, active, onClaimed }) => {
  const { addPromoCode, seasonalPromo } = useCart();
  const [idx, setIdx] = useState(0);
  const [copied, setCopied] = useState("");
  const IconComp = ICONS[slide.icon] || Sparkles;
  const messages = (slide.messages || []).filter(Boolean);
  const accent = slide.accent_color || C.accent;
  const tid = (name) => (active ? name : undefined);

  useEffect(() => {
    if (!active || messages.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % messages.length), 5000);
    return () => clearInterval(t);
  }, [active, messages.length]);

  const activeMessage = messages[idx] || slide.subtext || "";

  const handleAction = async () => {
    const pct = slide.discount_type === "percent" ? Number(slide.discount_amount) || 0 : 0;
    const amt = slide.discount_type === "amount" ? Number(slide.discount_amount) || 0 : 0;
    addPromoCode(slide.promo_code, pct, amt);
    try { await navigator.clipboard?.writeText(slide.promo_code); } catch { /* ignore */ }
    setCopied("Applied to your cart");
    window.setTimeout(() => { setCopied(""); onClaimed?.(); }, 1800);
  };

  return (
    <div
      data-testid={tid("rotator-slide-promo-code")}
      className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-12"
    >
      <div className="min-w-0 max-w-2xl">
        <span
          className="inline-flex items-center gap-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.22em]"
          style={{ color: accent }}
        >
          <IconComp className="w-3.5 h-3.5" strokeWidth={2} />
          {slide.eyebrow_label || "Special Offer"}
        </span>
        <div className="mt-3 flex items-start gap-3">
          <h2
            data-testid={tid("rotator-promo-headline")}
            className="font-display font-bold text-[1.6rem] sm:text-3xl md:text-[2.1rem] leading-[1.12] tracking-tight"
            style={{ color: C.ink }}
          >
            {slide.headline_accent ? (
              <><span style={{ color: slide.headline_accent_color }}>{slide.headline_accent}</span> {slide.headline}</>
            ) : slide.headline}
          </h2>
          {slide.ghost && <GhostMark className="w-9 h-9 md:w-10 md:h-10" testId={tid("rotator-sale-ghost")} />}
        </div>
        {activeMessage && (
          <p
            data-testid={tid("rotator-promo-message")}
            key={idx}
            className="promo-msg mt-3 text-sm md:text-[15px] leading-relaxed min-h-[44px] max-w-xl"
            style={{ color: C.muted, animation: "promoMsgIn 0.5s ease" }}
          >
            {activeMessage}
          </p>
        )}
        {slide.type === "sale" && active && (
          <SaleCountdown endDate={slide.end_date} testId="rotator-sale-countdown" className="mt-4 flex-wrap" />
        )}
      </div>

      <div className="shrink-0 flex flex-col items-start md:items-end gap-2">
        {slide.promo_code ? (
          <>
            <button
              type="button"
              onClick={handleAction}
              tabIndex={active ? 0 : -1}
              data-testid={tid("rotator-promo-cta")}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#8B2252]/50"
              style={{ background: accent }}
            >
              {copied && <Check className="w-4 h-4" strokeWidth={2.25} />}
              <span className="font-display font-bold tracking-[0.12em]">
                {copied || slide.cta_label || `Claim ${slide.promo_code}`}
              </span>
            </button>
            <span data-testid={tid("rotator-promo-footnote")} className="text-[11px] tracking-wide" style={{ color: C.muted }}>
              {seasonalPromo
                ? `Not combinable with ${seasonalPromo.name} items in your cart.`
                : "Stacks with your other coupons at checkout."}
            </span>
          </>
        ) : (
          <span
            data-testid={tid("rotator-promo-auto-badge")}
            className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em]"
            style={{ borderColor: "rgba(139,34,82,0.22)", color: accent, background: "rgba(139,34,82,0.04)" }}
          >
            <Check className="w-3.5 h-3.5" strokeWidth={2.25} />
            {slide.cta_label || "Applied automatically"}
          </span>
        )}
      </div>
    </div>
  );
};

export default PromoBannerRotator;
